import { useClient } from '@cortex/react-query';
import type { ModelConfig } from '@cortex/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { PageHeader } from '../../components/common/PageHeader';
import { routes } from '../../lib/constants';
import {
  applyModelParamFields,
  emptyModelParamFields,
  type IModelParamFields,
} from '../../lib/model-params';

export const Route = createFileRoute('/models/new')({ component: NewModelPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const grid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--sp-3)',
};

function NewModelPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [name, setName] = useState('');
  const [provider, setProvider] = useState('OPENAI');
  const [modelId, setModelId] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [params, setParams] = useState<IModelParamFields>(emptyModelParamFields);
  const setParam = (key: keyof IModelParamFields, value: string) =>
    setParams((p) => ({ ...p, [key]: value }));

  const create = useMutation({
    mutationFn: () =>
      client.http
        .post('v1/model-configs', {
          json: {
            name,
            provider,
            modelId,
            endpoint: endpoint.trim() || undefined,
            // Top-level `creds` is the plaintext credential — cortex-api
            // encrypts at rest via the keyring and serves a `hasCreds`
            // boolean back. Anything inside `config` is treated as
            // free-form non-secret JSON (temperature, maxTokens, etc.).
            ...(apiKey.trim() ? { creds: apiKey.trim() } : {}),
            config: applyModelParamFields(null, params),
          },
        })
        .json<ModelConfig>(),
    onSuccess: async (m) => {
      try {
        await qc.invalidateQueries({ queryKey: ['model-configs'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.modelDetail(m.id) });
      } catch (err) {
        console.error(err);
      }
    },
  });

  return (
    <>
      <PageHeader
        title="Add model config"
        actions={<BackButton to={routes.MODELS} label="Models" />}
      />
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-body">
          <form
            style={stack3}
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && modelId.trim()) create.mutate();
            }}
          >
            <div style={grid2}>
              <label className="field">
                <span className="field-label">Display name</span>
                <input
                  required={true}
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="anthropic-prod"
                />
              </label>
              <label className="field">
                <span className="field-label">Provider</span>
                <select
                  className="select-native"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <option value="OPENAI">OPENAI</option>
                  <option value="ANTHROPIC">ANTHROPIC</option>
                  <option value="AZURE_OPENAI">AZURE_OPENAI</option>
                  <option value="BEDROCK">BEDROCK</option>
                  <option value="VERTEX">VERTEX</option>
                  <option value="OLLAMA">OLLAMA</option>
                  <option value="OPENAI_COMPATIBLE">OPENAI_COMPATIBLE</option>
                  <option value="GOOGLE">GOOGLE</option>
                </select>
              </label>
            </div>
            <div style={grid2}>
              <label className="field">
                <span className="field-label">Model ID</span>
                <input
                  required={true}
                  className="input"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  placeholder="claude-sonnet-4-6"
                />
              </label>
              <label className="field">
                <span className="field-label">Endpoint (optional)</span>
                <input
                  className="input"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="https://…"
                />
              </label>
            </div>
            <label className="field">
              <span className="field-label">API key</span>
              <input
                type="password"
                className="input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-…"
                autoComplete="new-password"
              />
              <span className="field-help">
                Encrypted at rest. Optional for providers like Ollama that don&apos;t require one.
              </span>
            </label>
            <fieldset style={{ border: 'none', padding: 0, margin: 0, ...stack3 }}>
              <legend className="field-label" style={{ padding: 0 }}>
                Generation parameters (optional)
              </legend>
              <div style={grid2}>
                <label className="field">
                  <span className="field-label">Temperature</span>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    className="input"
                    value={params.temperature}
                    onChange={(e) => setParam('temperature', e.target.value)}
                    placeholder="model default"
                  />
                </label>
                <label className="field">
                  <span className="field-label">Max output tokens</span>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="200000"
                    className="input"
                    value={params.maxOutputTokens}
                    onChange={(e) => setParam('maxOutputTokens', e.target.value)}
                    placeholder="model default"
                  />
                </label>
              </div>
              <label className="field">
                <span className="field-label">Top P</span>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  className="input"
                  value={params.topP}
                  onChange={(e) => setParam('topP', e.target.value)}
                  placeholder="model default"
                />
              </label>
              <span className="field-help">
                Applied to every call this config drives. Leave blank to use the provider default.
                Anthropic ignores temperature/top-P while extended thinking is active, and caps
                temperature at 1 (values above 1 are for OpenAI-compatible endpoints only).
              </span>
            </fieldset>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <button type="submit" className="btn btn--primary" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create'}
              </button>
              {create.error ? (
                <span
                  className="t-xs"
                  style={{ color: 'var(--status-danger)', alignSelf: 'center' }}
                >
                  {(create.error as Error).message}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
